// composables/useSizeSorting.ts

import { computed } from 'vue'
import { CLOTHING_ORDER } from '~/constants/clothingOrder'

export interface FilterValue {
  name: string
  productCount: number
  id: number
  value: string
}

export interface SizeFilter {
  slug: string
  values: FilterValue[]
  // add other filter properties as needed
}

type SortKey =
  | { type: 'number'; value: number }
  | { type: 'range'; start: number; end: number }
  | { type: 'waist'; value: number }
  | { type: 'age'; value: number }
  | { type: 'clothing'; value: number }
  | { type: 'other'; value: number }

export const useSizeSorting = () => {
  // Clothing size order mapping

  const getSortKey = (item: FilterValue): SortKey => {
    const name = item.name.trim().toUpperCase()
    const normalized = name.replace(/[\u2010-\u205F\u2212]/g, '-')

    // 1. Detect pure numeric sizes (28, 29, 30, 30.5, 7.5)
    if (/^\d+(\.\d+)?$/.test(normalized)) {
      return { type: 'number', value: parseFloat(normalized) }
    }

    // 2. Detect decimal sizes that convert to fractions (7.5, 7.25, 7.625)
    const maybeDecimal = parseFloat(normalized)
    if (!isNaN(maybeDecimal) && normalized.includes('.')) {
      return { type: 'number', value: maybeDecimal }
    }

    // 3. RANGE sizes (37-39, 40-42, etc.)
    const rangeMatch = normalized.match(/^(\d+)-(\d+)$/)
    if (rangeMatch) {
      const start = parseInt(rangeMatch[1])
      const end = parseInt(rangeMatch[2])
      return { type: 'range', start, end }
    }

    // 4. WAIST/INSEAM sizes (30/32, 31/32, 34/32)
    const slashMatch = normalized.match(/^(\d+)\/(\d+)$/) //
    if (slashMatch) {
      const waist = parseInt(slashMatch[1])
      return { type: 'waist', value: waist }
    }

    // 5. Month ranges like "0M-6M"
    const monthMatch = normalized.match(/^(\d+)M?-(\d+)M?$/) //
    if (monthMatch) {
      return { type: 'age', value: parseInt(monthMatch[1]) }
    }

    // 6. Clothing sizes (XS, S, M, L, XL, XXL)
    if (CLOTHING_ORDER[normalized]) {
      return { type: 'clothing', value: CLOTHING_ORDER[normalized] }
    }

    // 7. Clothing ranges like XS/S, M/L
    const clothingRange = normalized.match(/^([A-Z]+)\/([A-Z]+)$/)
    if (clothingRange) {
      const a = CLOTHING_ORDER[clothingRange[1]]
      const b = CLOTHING_ORDER[clothingRange[2]]
      if (a && b) {
        return { type: 'clothing', value: Math.min(a, b) }
      }
    }

    // 8. Everything else alphabetically
    return { type: 'other', value: normalized.charCodeAt(0) } // <-- Use normalized
  }

  const sortSizes = (a: FilterValue, b: FilterValue): number => {
    const keyA = getSortKey(a)
    const keyB = getSortKey(b)

    // Priority order: numbers → age ranges → clothing → others
    const priority = {
      number: 1,
      waist: 2,
      range: 3,
      age: 4,
      clothing: 5,
      other: 6,
    } as const

    if (keyA.type === keyB.type) {
      return keyA.value - keyB.value
    }

    return priority[keyA.type] - priority[keyB.type]
  }

  const sortSizeValues = (values: FilterValue[]): FilterValue[] => {
    return [...values].sort(sortSizes)
  }

  const sortFiltersWithSizes = (filters: SizeFilter[]): SizeFilter[] => {
    return filters.map((filter) => {
      if (filter.slug === 'size') {
        return {
          ...filter,
          values: sortSizeValues(filter.values),
        }
      }
      return filter
    })
  }

  // For reactive data
  const sortedFilters = (filters: SizeFilter[]) => {
    return computed(() => sortFiltersWithSizes(filters))
  }

  // Debug helper to see sort keys
  const debugSortKey = (
    item: FilterValue,
  ): { item: FilterValue; sortKey: SortKey } => {
    return {
      item,
      sortKey: getSortKey(item),
    }
  }

  return {
    getSortKey,
    sortSizes,
    sortSizeValues,
    sortFiltersWithSizes,
    sortedFilters,
    debugSortKey,
    CLOTHING_ORDER,
  }
}
